<script setup>
// ContactDetailSidebar.vue — sidebar direita slim (~280px) com cards
// compactos: About, Etiquetas, Conversas mini.
//
// Mostra somente leitura. Acoes de edicao acontecem nas tabs principais.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';
import countries from 'shared/constants/countries';

import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversationsCount: { type: Number, default: 0 },
});

const emit = defineEmits(['viewAllConversations']);

const { t } = useI18n();

const inboxesGetter = useMapGetter('inboxes/getInboxById');

const contactInboxes = computed(() => props.contact?.contactInboxes || []);

const preferredInbox = computed(() => {
  const first = contactInboxes.value[0];
  if (!first?.inbox?.id) return null;
  return inboxesGetter.value(first.inbox.id) || first.inbox;
});

const channelLabel = computed(() => {
  if (!preferredInbox.value) return '';
  return preferredInbox.value.name || preferredInbox.value.channel_type || '';
});

const createdAt = computed(() =>
  props.contact?.createdAt ? dynamicTime(props.contact.createdAt) : null
);

const lastActivityAt = computed(() =>
  props.contact?.lastActivityAt
    ? dynamicTime(props.contact.lastActivityAt)
    : null
);

const countryDetails = computed(() => {
  const attrs = props.contact?.additionalAttributes || {};
  const { country, countryCode, city } = attrs;
  if (!country && !countryCode && !city) return null;
  const match = countries.find(
    c => c.id === countryCode || c.code === country || c.id === country
  );
  return {
    countryCode: match?.id || null,
    name: match?.name || country || null,
    city: city || null,
  };
});

const locationLabel = computed(() => {
  if (!countryDetails.value) return '';
  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(', ');
});

const companyName = computed(
  () => props.contact?.additionalAttributes?.companyName || ''
);
</script>

<template>
  <aside
    class="w-[280px] shrink-0 border-l border-n-weak bg-n-solid-1 overflow-y-auto"
  >
    <div class="flex flex-col gap-3 p-4">
      <!-- About card -->
      <section
        class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-background"
      >
        <h3 class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
          {{ t('CONTACT_VIEW.ABOUT.TITLE') }}
        </h3>
        <dl class="flex flex-col gap-2.5 text-sm">
          <div v-if="contact?.id" class="flex items-start gap-2">
            <dt
              class="w-20 shrink-0 text-xs uppercase tracking-wide text-n-slate-10 pt-0.5"
            >
              {{ t('CONTACT_VIEW.ABOUT.ID') }}
            </dt>
            <dd class="text-n-slate-12 break-all">{{ `#${contact.id}` }}</dd>
          </div>
          <div v-if="createdAt" class="flex items-start gap-2">
            <dt
              class="w-20 shrink-0 text-xs uppercase tracking-wide text-n-slate-10 pt-0.5"
            >
              {{ t('CONTACT_VIEW.ABOUT.CREATED') }}
            </dt>
            <dd class="text-n-slate-12">{{ createdAt }}</dd>
          </div>
          <div v-if="lastActivityAt" class="flex items-start gap-2">
            <dt
              class="w-20 shrink-0 text-xs uppercase tracking-wide text-n-slate-10 pt-0.5"
            >
              {{ t('CONTACT_VIEW.ABOUT.LAST_ACTIVITY') }}
            </dt>
            <dd class="text-n-slate-12">{{ lastActivityAt }}</dd>
          </div>
          <div v-if="preferredInbox" class="flex items-start gap-2">
            <dt
              class="w-20 shrink-0 text-xs uppercase tracking-wide text-n-slate-10 pt-0.5"
            >
              {{ t('CONTACT_VIEW.ABOUT.CHANNEL') }}
            </dt>
            <dd>
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-n-blue-3 text-n-blue-11 text-xs"
              >
                <span class="i-lucide-radio size-3" />
                <span class="truncate max-w-[120px]">{{ channelLabel }}</span>
              </span>
            </dd>
          </div>
          <div v-if="locationLabel" class="flex items-start gap-2">
            <dt
              class="w-20 shrink-0 text-xs uppercase tracking-wide text-n-slate-10 pt-0.5"
            >
              {{ t('CONTACT_VIEW.ABOUT.LOCATION') }}
            </dt>
            <dd class="flex items-center gap-1.5 text-n-slate-12 min-w-0">
              <Flag
                v-if="countryDetails?.countryCode"
                :country="countryDetails.countryCode"
                class="size-3.5 shrink-0"
              />
              <span class="truncate">{{ locationLabel }}</span>
            </dd>
          </div>
          <div v-if="companyName" class="flex items-start gap-2">
            <dt
              class="w-20 shrink-0 text-xs uppercase tracking-wide text-n-slate-10 pt-0.5"
            >
              {{ t('CONTACT_VIEW.ABOUT.COMPANY') }}
            </dt>
            <dd class="text-n-slate-12 truncate">{{ companyName }}</dd>
          </div>
        </dl>
      </section>

      <!-- Tags card -->
      <section
        class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-background"
      >
        <h3 class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
          {{ t('CONTACT_VIEW.ABOUT.TAGS') }}
        </h3>
        <ContactLabels :contact-id="contact?.id" />
      </section>

      <!-- Conversas mini -->
      <section
        class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-background"
      >
        <div class="flex flex-col">
          <span
            class="text-xs font-medium uppercase tracking-wide text-n-slate-11"
          >
            {{ t('CONTACT_VIEW.ABOUT.CONVERSATIONS') }}
          </span>
          <span class="text-lg font-medium text-n-slate-12">
            {{ conversationsCount }}
          </span>
        </div>
        <button
          v-if="conversationsCount > 0"
          type="button"
          class="text-sm text-n-blue-text hover:underline transition-colors"
          @click="emit('viewAllConversations')"
        >
          {{ t('CONTACT_VIEW.ABOUT.VIEW_ALL') }}
        </button>
      </section>
    </div>
  </aside>
</template>
